import React, { useEffect, useRef, useState } from "react";
import { X, Music, Search as SearchIcon, Play, Pause, Check, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * MusicPicker — modal for searching & previewing 30-sec music snippets.
 * Results come from /api/music/search (iTunes + optional Spotify).
 * Selecting a track calls onSelect(track) and closes the modal.
 */
export default function MusicPicker({ open, onClose, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) {
      stopAudio();
      setQ("");
      setResults([]);
    }
  }, [open]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/music/search?q=${encodeURIComponent(q.trim())}&limit=24`);
      setResults(data.results || []);
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = async (track) => {
    if (playingId === track.id) {
      stopAudio();
      return;
    }
    stopAudio();
    let url = track.preview_url;
    // SoundCloud results come back with no preview_url to keep search
    // fast. Resolve the playable progressive URL on the first click.
    if (!url) {
      setPlayingId(`loading-${track.id}`);
      try {
        const { data } = await api.get(`/music/track/${encodeURIComponent(track.id)}`);
        url = data?.preview_url || null;
        if (data?.preview_url) {
          // Cache the resolved URL on the in-memory list so a re-click
          // doesn't trigger another resolve round-trip.
          setResults((rs) => rs.map((r) => r.id === track.id ? { ...r, preview_url: data.preview_url } : r));
        }
      } catch { /* fall through — show "no preview" */ }
    }
    if (!url) {
      setPlayingId(null);
      return;
    }
    const a = new Audio(url);
    a.volume = 0.8;
    a.play().catch(() => {});
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(track.id);
  };

  const pick = (track) => {
    stopAudio();
    onSelect?.(track);
    onClose?.();
  };

  if (!open) return null;
  return (
    <div
      data-testid="music-picker-overlay"
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        data-testid="music-picker"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f0f0f] border border-white/10 w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col"
      >
        <header className="flex items-center gap-3 p-4 border-b border-white/10">
          <Music className="w-5 h-5 text-tv-accent" />
          <div className="flex-1 font-display font-bold text-white text-lg">Add music</div>
          <button onClick={onClose} data-testid="music-picker-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </header>

        <form onSubmit={search} className="p-4 border-b border-white/10">
          <div className="relative">
            <SearchIcon className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="music-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search songs, artists, albums…"
              className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2.5 text-white text-sm outline-none focus:border-tv-accent"
              autoFocus
            />
          </div>
        </form>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="py-10 flex items-center justify-center text-white/60">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="py-10 text-center text-white/50 text-sm px-6">
              Search the catalog for a track. 30-second previews play right here, then attach to your post.
            </div>
          )}
          {!loading && results.map((t) => (
            <div
              key={t.id}
              data-testid={`music-track-${t.id}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 border-b border-white/5"
            >
              <button
                onClick={() => togglePreview(t)}
                data-testid={`music-preview-${t.id}`}
                className="relative w-11 h-11 rounded overflow-hidden shrink-0 group"
                aria-label={playingId === t.id ? "Pause preview" : "Play preview"}
              >
                {t.album_art ? (
                  <img src={t.album_art} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/10" />
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-90">
                  {playingId === t.id ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white fill-white" />}
                </div>
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold truncate">{t.title}</div>
                <div className="text-white/50 text-xs truncate">{t.artist}{t.album ? ` · ${t.album}` : ""}</div>
                {t.provider && (
                  <span
                    data-testid={`music-provider-${t.id}`}
                    className={`mt-0.5 inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                      t.provider === "soundcloud" ? "bg-orange-500/20 text-orange-300"
                      : t.provider === "spotify"  ? "bg-emerald-500/20 text-emerald-300"
                      :                             "bg-white/10 text-white/60"
                    }`}
                  >
                    {t.provider === "soundcloud" ? "SoundCloud" : t.provider === "spotify" ? "Spotify" : "iTunes"}
                  </span>
                )}
              </div>
              <button
                data-testid={`music-select-${t.id}`}
                onClick={() => pick(t)}
                className="shrink-0 bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-1.5 rounded-full flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Use
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
